/**
 * Series: copilot-agent-governance (#578, epic #557)
 *
 * Sequel to shared-tenant-content-boundary (#530), on the same composite
 * scenario: two organisations, one MSP-managed tenant.
 *
 * Pure data. No `@/` aliases or JSX so Node build scripts (sitemap, llms,
 * prerender manifest) can import it directly through scripts/lib/series.js.
 *
 * Posts are resolved by id from the blog data at render time. `plannedTitle`
 * is only a fallback for parts that are not published yet (shown unlinked as
 * "upcoming"); a published post always supplies its own title and excerpt.
 */
export default {
  slug: 'copilot-agent-governance',
  publishDate: '2027-01-01',
  title: 'Governing Copilot agents before they act: identity, knowledge, tools and evidence',
  shortTitle: 'Governing Copilot agents',
  // 140 to 160 characters
  description:
    'An eight-part series on governing Copilot agents in a shared Microsoft 365 tenant: who owns them, what they can read, what they can do, and what the logs prove.',
  intro:
    'An agent that only answers questions is a search problem. An agent that can act is an access problem. This series works through governing Copilot agents in a tenant shared by two organisations: who owns and sponsors an agent, how to find the agents already running, whose credentials each operation uses, what knowledge sources quietly expose, where tools turn an answer into an action, and what the audit trail can and cannot reconstruct afterwards. It is a design and a pilot plan, not a completed deployment.',
  audience:
    'CISOs, security and risk leads, Microsoft 365 administrators, architects, and managed service providers governing Copilot agents across one or more organisations.',
  whoItsFor: [
    'Security and risk leads who must answer who is accountable for an agent that acts on its own',
    'Microsoft 365 administrators asked to inventory agents that were created without a governance process',
    'Architects designing an agent that reads approved content and raises service requests, without crossing an organisational boundary',
    'Managed service providers running agents in a tenant shared by more than one organisation',
  ],
  whatYouWillLearn: [
    'Why agent ownership and sponsorship are separate roles, and which lifecycle actions a sponsor cannot perform',
    'How to find the agents already in the tenant, including shared, unmanaged and ownerless ones, and where the inventory has documented gaps',
    'Why an agent can act under a person’s delegated permissions or its own identity, and why the distinction changes per operation',
    'How knowledge sources become access paths, and why blocking uploaded documents does not block SharePoint',
    'Where runtime protection genuinely covers an action-capable agent, and where it is still preview or unsupported',
    'What the audit trail reconstructs after an agent acts, and which part of the evidence sits in a different product',
  ],
  // Parts in reading order. NOT numeric order: parts 1 and 2 reuse existing
  // posts 138 and 137, so the ids run 138, 137, then 147 onward. This is
  // deliberate. `getPartNumber` uses findIndex and SeriesPage renders in array
  // order, so the reading order is whatever this array says. Do not sort these
  // numerically: that would silently renumber parts 1 and 2.
  //
  // Ids 147-151 (and 152, held) are reserved in
  // src/data/blog-mdx/RESERVED-IDS.md. Several have no MDX file yet, so another
  // author scanning for "highest id + 1" would otherwise reuse them.
  parts: [
    { id: 138, plannedTitle: 'Who owns and sponsors an agent in the tenant?' },
    { id: 137, plannedTitle: 'Find the agents first: published, shared, unmanaged and ownerless' },
    { id: 147, plannedTitle: 'Whose credentials: user, maker, agent identity or app?' },
    { id: 148, plannedTitle: 'Knowledge is an access path: what SharePoint can reveal' },
    { id: 149, plannedTitle: 'Tools turn answers into actions: connectors, triggers and writes' },
    { id: 150, plannedTitle: 'Prompt injection meets an action-capable agent' },
    { id: 151, plannedTitle: 'The audit trail after the agent acts' },
  ],
  // Part 8 is deliberately not published until a pilot has run and been
  // verified. It carries no id and has no MDX file: the id is reserved, not
  // allocated. Do not add it to `parts` until there is a post to resolve.
  held: [
    {
      part: 8,
      title: 'A bounded pilot: useful operation and separation together',
      status: 'held pending a verified pilot',
    },
  ],
  totalParts: 8,
  methodology:
    'Reviewed design and pilot plan, not a completed deployment. Anonymised: Org A and Org B.',
  methodologyDetail:
    'Every technical claim in this series is traced to vendor or government documentation in a source ledger, and each post states the date that documentation was checked. The products covered here changed materially during 2026, so currency matters more than usual: where a capability is in preview, the post says so. Where the documentation is silent, the series says so and labels the conclusion as our reading. No client, tenant or managed service provider is identified, and no pilot results are reported until a pilot has actually run.',
  sourceLedgerNote:
    'Each post carries a Sources section naming the documentation pages behind its claims. Where documentation conflicts or is silent, the post labels the point as inference or recommendation rather than fact. A framework mapping is not AI assurance or certification.',
  faqs: [
    {
      question: 'What does it mean to govern a Copilot agent?',
      answer:
        'It means deciding four things and being able to evidence them: who is accountable for the agent, what it is allowed to read, what actions it is allowed to take, and what the logs show after it acts. Limiting who can chat with an agent is not one of them. Audience and data access are separate controls, and confusing the two is the most common mistake in this area.',
    },
    {
      question: 'Does restricting who can use an agent restrict what it can access?',
      answer:
        'No. Sharing controls govern who can start a conversation with an agent. They do not change what the agent is permitted to read or do once a conversation has started. Access is determined by the identity and permissions used for each operation, which may be the signed-in person’s delegated permissions or the agent’s own.',
    },
    {
      question: 'Can prompt injection be prevented?',
      answer:
        'No, and this series makes no such claim. Runtime protection can audit or block certain categories of action, and those categories are documented and finite. The series describes what is actually covered, names where coverage is still preview or unsupported, and sets out the test that would expose the residual risk rather than asserting it has been removed.',
    },
    {
      question: 'Do the audit logs show what an agent actually did?',
      answer:
        'Partly. Authoring events are well covered: who created, shared, published or changed the authentication on an agent. Reconstructing a specific interaction is harder, because the audit record carries metadata and a transcript identifier rather than the text itself, and the text sits in a different solution on a best-effort basis. Part 7 works through what that means for an investigation.',
    },
    {
      question: 'Which framework does this series map to?',
      answer:
        'ASD Essentials for cloud is the primary mapping, as it targets organisations running entirely on SaaS and PaaS, with the Essential Eight carried as a legacy cross-reference. That ordering reflects ASD’s 2026 transition. A mapping to any framework is an aid to planning, not assurance or certification.',
    },
    {
      question: 'Has this design been deployed or tested?',
      answer:
        'No. This is a reviewed design and a pilot plan. Part 8, which would report what a pilot proved, is held until a pilot has run and its results have been verified. Until then the series makes no claims about observed results.',
    },
    {
      question: 'Who are Org A and Org B?',
      answer:
        'They are anonymised labels for the two organisations in the worked example, carried over from the shared-tenant content boundary series so the two can be read together. No tenant, client, sector or managed service provider is identified.',
    },
  ],
  cta: {
    heading: 'Are your Copilot agents governed before they act?',
    text: 'Talk to us about reviewing agent ownership, knowledge sources and action boundaries in your tenant.',
    primary: {
      label: 'Assess your SharePoint and Teams governance',
      href: '/sharepoint-teams-governance',
    },
    secondary: { label: 'Contact EDUC4TE', href: '/contact' },
  },
  // Date the series page content was last reviewed (ISO). Used for dateModified.
  lastReviewed: '2026-10-20',
};
